<template>
  <article
    class="lead-new-lead-card ui-kanban-card ui-kanban-card--detailed"
    :class="[
      { 'lead-new-lead-card--highlighted': highlighted, 'ui-kanban-card--highlighted': highlighted },
      { 'lead-new-lead-card--multi-activity': hasMultipleActivities },
      activityTheme ? `lead-new-lead-card--activity-${activityTheme}` : ''
    ]"
    :style="activityCardStyle"
    role="button"
    tabindex="0"
    :aria-label="`Open ${leadName}`"
    @click="openLead"
    @keydown.enter.prevent="openLead"
  >
    <div class="lead-new-lead-card__identity ui-kanban-card__identity">
      <span
        v-tooltip="lead.country && lead.country.name ? { content: countryName, placement: 'top', offset: 8, classes: 'tooltip--new' } : null"
        class="lead-new-lead-card__country ui-kanban-card__country ui-tooltip-target"
        :tabindex="lead.country && lead.country.name ? 0 : null"
        :aria-label="countryName"
      >
        <span :class="`fflag ff-sm fflag-${countryCode}`" aria-hidden="true"></span>
      </span>
      <AppTableText :text="lead.name" fallback="Unnamed lead" class="lead-new-lead-card__name ui-kanban-card__name" />
      <span
        v-tooltip="Number.isFinite(partyCount) && partyTooltip !== 'Party not provided' ? { content: partyTooltip, placement: 'top', offset: 8, classes: 'tooltip--new' } : null"
        class="lead-new-lead-card__party ui-kanban-card__party ui-tooltip-target"
        :tabindex="Number.isFinite(partyCount) && partyTooltip !== 'Party not provided' ? 0 : null"
      >
        <img
          class="lead-new-lead-card__party-icon ui-kanban-card__party-icon"
          src="@/assets/img/personal-details.svg"
          alt=""
          aria-hidden="true"
        >
        <span>{{ partyCount }}</span>
      </span>
    </div>

    <div class="lead-new-lead-card__details ui-kanban-card__details">
      <div

        class="lead-new-lead-card__communication ui-kanban-card__communication"
      >
        <img
          v-if="communicationAsset"
          class="lead-new-lead-card__communication-icon ui-kanban-card__communication-icon"
          :class="`lead-new-lead-card__communication-icon--${communicationType}`"
          :src="communicationAsset"
          alt=""
          aria-hidden="true"
        >
        <AppTableText :text="communicationType ? contactSummary : null" fallback="No contact details" :tooltip="contactTooltip" />
      </div>
      <div
        v-if="travelLabel"

        class="lead-new-lead-card__travel ui-kanban-card__travel"
      >
        <span class="lead-new-lead-card__travel-icon ui-kanban-card__travel-icon" aria-hidden="true"></span>
        <AppTableText :text="travelLabel" />
      </div>
    </div>

    <div class="lead-new-lead-card__tags ui-kanban-card__tags">
      <LeadManagementGuidanceIcon :status="lead.management_guidance_status" :comment="lead.management_guidance_comment" />
      <AppStatusBadge
        v-if="sourceName"
        v-tooltip="lead.source && lead.source.code && lead.source.code !== sourceName ? { ...sourceTooltip, classes: 'tooltip--new' } : null"
        design="new"
        tone="blue"
        class="lead-new-lead-card__source ui-kanban-card__source ui-tooltip-target"
        :tabindex="lead.source && lead.source.code && lead.source.code !== sourceName ? 0 : null"
        :label="sourceName"
      />
      <LeadActivityIcons :label-tooltips="false" keyboard-tooltips design="new" icon-set="crm" class="lead-new-lead-card__activities ui-kanban-card__activities" :activities="lead.activities" />
    </div>

    <div class="lead-new-lead-card__footer ui-kanban-card__footer">
      <span
        v-tooltip="waitingMinutes !== null ? { content: footerTooltip, placement: 'top', offset: 8, classes: 'tooltip--new' } : null"
        class="lead-new-lead-card__waiting ui-kanban-card__waiting ui-tooltip-target"
        :tabindex="waitingMinutes !== null ? 0 : null"
          :class="[`lead-new-lead-card__waiting--${waitingTone}`, `ui-kanban-card__waiting--${waitingTone}`]"
      >
        {{ waitingLabel }}
      </span>
    </div>
  </article>
</template>

<script>
import AppTableText from '@/components/AppTableText.vue'
import LeadActivityIcons from '@/views/crm/task-tracker/LeadActivityIcons.vue'
import LeadManagementGuidanceIcon from '@/views/crm/task-tracker/LeadManagementGuidanceIcon.vue'
import { formatTravelMonth } from '@/views/crm/task-tracker/leadFlowDate'

const COMMUNICATION_OPTIONS = {
  mail: {
    name: 'Email',
    asset: require('@/assets/img/email-icon.svg')
  },
  phone: {
    name: 'Phone',
    asset: require('@/assets/img/phone-icon.svg')
  },
  whatsapp: {
    name: 'WhatsApp',
    asset: require('@/assets/img/wa-icon.svg')
  },
  telegram: {
    name: 'Telegram',
    asset: require('@/assets/img/tg-icon.svg')
  }
}

const ACTIVITY_ALIASES = {
  day_trips: 'day_trip',
  island_vacation: 'island'
}

const ACTIVITY_CARD_OPTIONS = {
  safari: {
    background: '#fff4df',
    border: '#eb9514'
  },
  climbing: {
    background: '#edf6e5',
    border: '#219b52'
  },
  day_trip: {
    background: '#e8f6fc',
    border: '#3d9dd3'
  },
  island: {
    background: '#e6f7f7',
    border: '#28a8aa'
  }
}

export default {
  name: 'LeadNewLeadCard',
  components: {
    AppTableText,
    LeadActivityIcons,
    LeadManagementGuidanceIcon
  },
  props: {
    lead: {
      type: Object,
      required: true
    },
    now: {
      type: Number,
      required: true
    },
    highlighted: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    contact() {
      return this.lead.contact || {}
    },
    countryCode() {
      return this.lead.country && this.lead.country.code ? this.lead.country.code : 'empty'
    },
    countryName() {
      return this.lead.country && this.lead.country.name ? this.lead.country.name : 'Country not provided'
    },
    leadName() {
      return this.lead.name || 'Unnamed lead'
    },
    sourceName() {
      return this.lead.source && this.lead.source.name ? this.lead.source.name : ''
    },
    sourceTooltip() {
      const sourceCode = this.lead.source && this.lead.source.code
      return {
        content: sourceCode || this.sourceName,
        placement: 'top',
        offset: 8
      }
    },
    partyCount() {
      const party = this.lead.party || {}
      return Number.isFinite(party.total) ? party.total : '—'
    },
    partyTooltip() {
      const party = this.lead.party || {}
      const parts = [
        this.partyPart('Adults', party.adults),
        this.partyPart('Teenagers', party.teenagers),
        this.partyPart('Children', party.children)
      ].filter(Boolean)
      return parts.length ? parts.join(' · ') : 'Party not provided'
    },
    travelLabel() {
      const travel = this.lead.travel || {}
      const travelMonth = formatTravelMonth(travel.date)
      if (travelMonth) return travelMonth
      if (travel.planning_period && travel.planning_period.name) return travel.planning_period.name
      return ''
    },
    preferredCommunication() {
      const value = (this.contact.preferred_method || '').trim()
      const code = value.toLowerCase()
      if (!code) return null
      if (code.includes('whatsapp')) return { icon: 'whatsapp', name: 'WhatsApp' }
      if (code.includes('telegram')) return { icon: 'telegram', name: 'Telegram' }
      if (code.includes('email')) return { icon: 'mail', name: 'Email' }
      if (code.includes('phone') || code.includes('call')) return { icon: 'phone', name: 'Phone' }
      return null
    },
    contactValues() {
      return {
        mail: this.normalizeContactValue(this.contact.email),
        phone: this.normalizeContactValue(this.contact.phone),
        whatsapp: this.normalizeContactValue(this.contact.whatsapp),
        telegram: this.normalizeContactValue(this.contact.telegram)
      }
    },
    communicationType() {
      const preferredType = this.preferredCommunication && this.preferredCommunication.icon
      const availability = {
        mail: Boolean(this.contactValues.mail || this.contact.has_email),
        phone: Boolean(this.contactValues.phone || this.contact.has_phone),
        whatsapp: Boolean(
          this.contactValues.whatsapp
          || (preferredType === 'whatsapp' && (this.contactValues.phone || this.contact.has_phone))
        ),
        telegram: Boolean(this.contactValues.telegram || preferredType === 'telegram')
      }

      if (preferredType && availability[preferredType]) return preferredType
      if (availability.mail) return 'mail'
      if (availability.phone) return 'phone'
      if (availability.whatsapp) return 'whatsapp'
      if (availability.telegram) return 'telegram'
      return preferredType || ''
    },
    communicationAsset() {
      const option = COMMUNICATION_OPTIONS[this.communicationType]
      return option ? option.asset : ''
    },
    contactSummary() {
      const option = COMMUNICATION_OPTIONS[this.communicationType]
      if (!option) return 'No contact details'

      return option.name
    },
    contactTooltip() {
      const option = COMMUNICATION_OPTIONS[this.communicationType]
      if (!option) return 'No contact details'

      let value = this.contactValues[this.communicationType]
      if (this.communicationType === 'whatsapp') {
        value = this.contactValues.whatsapp || this.contactValues.phone
      }
      return value ? `${option.name} · ${value}` : option.name
    },
    hasActivities() {
      return this.activityThemes.length > 0
    },
    activityThemes() {
      const activities = Array.isArray(this.lead.activities) ? this.lead.activities : []
      return [
        ...new Set(activities.map(activity => {
          const code = typeof activity === 'string' ? activity : activity && activity.code
          return ACTIVITY_ALIASES[code] || code
        }).filter(code => ACTIVITY_CARD_OPTIONS[code]))
      ]
    },
    activityTheme() {
      return this.activityThemes.length === 1 ? this.activityThemes[0] : ''
    },
    hasMultipleActivities() {
      return this.activityThemes.length > 1
    },
    activityCardStyle() {
      if (!this.hasMultipleActivities) return {}

      const lastColorStop = 78
      const stops = this.activityThemes.map((code, index) => {
        const position = Math.round((index * lastColorStop) / (this.activityThemes.length - 1))
        return `${ACTIVITY_CARD_OPTIONS[code].background} ${position}%`
      })
      const firstActivity = ACTIVITY_CARD_OPTIONS[this.activityThemes[0]]
      return {
        background: `linear-gradient(135deg, ${stops.join(', ')}, #fff 100%)`,
        borderLeftColor: firstActivity.border
      }
    },
    requestExcerpt() {
      return typeof this.lead.request_excerpt === 'string' ? this.lead.request_excerpt.trim() : ''
    },
    footerTooltip() {
      return [this.waitingSinceLabel, this.requestExcerpt].filter(Boolean).join(' | ')
    },
    waitingAt() {
      return this.lead.waiting_since || this.lead.created_at || ''
    },
    waitingSinceLabel() {
      const waitingAt = new Date(this.waitingAt)
      return Number.isNaN(waitingAt.getTime()) ? 'Waiting time not provided' : waitingAt.toLocaleString()
    },
    waitingMinutes() {
      const waitingAt = new Date(this.waitingAt).getTime()
      if (Number.isNaN(waitingAt)) return null

      return Math.max(0, (this.now - waitingAt) / 60000)
    },
    waitingTone() {
      if (this.waitingMinutes === null || this.waitingMinutes < 15) return 'neutral'
      if (this.waitingMinutes <= 30) return 'warning'
      return 'danger'
    },
    waitingLabel() {
      if (this.waitingMinutes === null) return 'Waiting unknown'

      const minutes = Math.floor(this.waitingMinutes)
      if (minutes < 60) return `Waiting ${minutes}m`

      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `Waiting ${hours}h ${minutes % 60}m`
      return `Waiting ${Math.floor(hours / 24)}d ${hours % 24}h`
    }
  },
  methods: {
    openLead(event) {
      this.$emit('open', {
        leadId: this.lead.id,
        triggerElement: event.currentTarget
      })
    },
    partyPart(label, value) {
      return Number.isFinite(value) ? `${label}: ${value}` : null
    },
    normalizeContactValue(value) {
      return typeof value === 'string' ? value.trim() : ''
    }
  }
}
</script>

<style scoped>

.lead-new-lead-card::after {
  position: absolute;
  z-index: 0;
  top: 48px;
  right: -18px;
  width: 92px;
  height: 92px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 70px;
  content: '';
  opacity: .045;
}

.lead-new-lead-card > * {
  position: relative;
  z-index: 1;
}

.lead-new-lead-card--activity-safari {
  border-left-color: #eb9514;
  background: linear-gradient(135deg, #fff7e9 0%, #fffcf6 56%, #fff 100%);
}

.lead-new-lead-card--activity-safari::after {
  background-image: url('~@/assets/img/lead-flow/activity-safari.png');
}

.lead-new-lead-card--activity-climbing {
  border-left-color: #219b52;
  background: linear-gradient(135deg, #f4f8ec 0%, #fbfdf8 56%, #fff 100%);
}

.lead-new-lead-card--activity-climbing::after {
  background-image: url('~@/assets/img/lead-flow/activity-climbing.png');
}

.lead-new-lead-card--activity-day_trip {
  border-left-color: #3d9dd3;
  background: linear-gradient(135deg, #edf8fd 0%, #f8fcfe 56%, #fff 100%);
}

.lead-new-lead-card--activity-day_trip::after {
  background-image: url('~@/assets/img/lead-flow/activity-day-trip.png');
}

.lead-new-lead-card--activity-island {
  border-left-color: #28a8aa;
  background: linear-gradient(135deg, #ecf9f9 0%, #f8fdfd 56%, #fff 100%);
}

.lead-new-lead-card--activity-island::after {
  background-image: url('~@/assets/img/lead-flow/activity-island.png');
}

.lead-new-lead-card--multi-activity::after {
  display: none;
}

</style>
